import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { TopicFilters } from "@circle-react/components/Spaces/TopicFilters";
import { useFilters } from "@circle-react/components/Spaces/TopicFilters/hooks/useFilters";
import { useCurrentSpaceContext } from "@circle-react/contexts";
import { PostsDetailsContextProvider } from "@circle-react/contexts/postsDetailsContext";
import { posts } from "@circle-react/helpers/urlHelpers";
import { usePostList } from "@circle-react/hooks/posts/usePostList";
import { useIsLoadingAppData } from "@circle-react/hooks/useIsLoadingAppData";
import type { Post } from "@circle-react/types";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { STATUS } from "../../helpers/constants";
import { useThumbnailView } from "../hooks/useThumbnailView";
import { EventsList } from "./EventsList";
import { EventsStatusFilter } from "./EventsStatusFilter";
import { NoUpcomingEvents } from "./NoUpcomingEvents";
import { PastEvents } from "./PastEvents";
import { Separator } from "./Separator";

export interface NewListViewProps {
  pastEvents?: boolean;
  sort?: string;
  status?: string;
}

export const NewListView = ({ sort, pastEvents, status }: NewListViewProps) => {
  const { data: space } = useCurrentSpaceContext();
  const isLoadingAppData = useIsLoadingAppData();

  const { currentFilters } = useFilters();
  const hasSelectedFilters = !!currentFilters.size;
  const {
    posts: events,
    mapPages,
    isLoading,
    hasNextPage,
    fetchNextPage,
    onPostDestroy,
    onPostChange,
  } = usePostList({
    sort,
    past_events: pastEvents,
    used_on: space?.display_view,
    status: status,
    topics: Array.from(currentFilters),
    include_top_pinned_post: true,
  });
  const perPagePostIds = mapPages((page: any) =>
    page.records.map((post: Post) => post.id),
  );
  const isDraft = status === STATUS.DRAFT;
  const hasDraftEvents = space?.has_drafts;
  const isUpcomingEvents = !(pastEvents || isDraft);
  const isNextEventEnabled = Boolean(space?.show_next_event);
  const isThumbnailView = useThumbnailView();

  const history = useHistory();

  useEffect(() => {
    const shouldRedirectToPostsIndexPage =
      !isLoading && isDraft && !hasDraftEvents;
    if (shouldRedirectToPostsIndexPage) {
      history.push(
        posts.index({
          spaceSlug: space?.slug,
        }),
      );
    }
  }, [isDraft, isLoading, history, space]);

  const activeSort = sort || "asc";
  const pinnedEvent = events.find(t => t.pinned_at_top_of_space);
  const unPinnedEvents = events.filter(t => !t.pinned_at_top_of_space);
  const hasTopics = Number(space?.topics_count) > 0;
  const skeletonVariant = isThumbnailView
    ? "new-events-thumbnail-view"
    : "new-events-list-view";

  if (isLoadingAppData) {
    return <SkeletonLoader variant={skeletonVariant} />;
  }

  if (!space) {
    return null;
  }

  return (
    <div className="mx-auto max-w-2xl p-0">
      <div className="mb-6 mt-2 flex gap-x-3 py-2 md:mt-0 md:gap-x-4">
        <EventsStatusFilter space={space} />
        {hasTopics && (
          <>
            <Separator />
            <TopicFilters inline />
          </>
        )}
      </div>
      {isLoading ? (
        <SkeletonLoader variant={skeletonVariant} />
      ) : unPinnedEvents.length === 0 && !pinnedEvent ? (
        <>
          <NoUpcomingEvents
            isPastEvents={Boolean(pastEvents)}
            isDraft={isDraft}
            isFilteredByTopic={hasSelectedFilters}
          />
          {isUpcomingEvents && !hasSelectedFilters && (
            <PastEvents spaceId={space?.id} isThumbnailView={isThumbnailView} />
          )}
        </>
      ) : (
        <PostsDetailsContextProvider
          perPagePostIds={perPagePostIds}
          spaceId={space?.id}
        >
          <EventsList
            activeSort={activeSort}
            pinnedEvent={pinnedEvent}
            events={unPinnedEvents}
            isPast={Boolean(pastEvents)}
            isDraft={isDraft}
            hasNextPage={Boolean(hasNextPage)}
            fetchNextPage={fetchNextPage}
            onPostDestroy={onPostDestroy}
            onPostChange={onPostChange}
            isNextEventEnabled={isNextEventEnabled}
            isThumbnailView={isThumbnailView}
          />
        </PostsDetailsContextProvider>
      )}
    </div>
  );
};
1;
